import {
  getKeywordsByType,
  IProduct,
  keywordsToText,
} from "integrations/firebase/collections";

export const DATABASE_KEYWORD = "database";
// Regenerating keywords promotes prior generated ones to custom; custom is derived from database + current generated only.
export const CUSTOM_KEYWORD = "custom";
export const GENERATED_KEYWORD = "generated";

export enum KeywordType {
  Database = DATABASE_KEYWORD,
  Custom = CUSTOM_KEYWORD,
  Generated = GENERATED_KEYWORD,
}

export interface Keyword {
  id: string;
  value: string;
  type: KeywordType;
}

const union = <T>(a: Set<T>, b: Set<T>): Set<T> => new Set([...a, ...b]);

const difference = <T>(a: Set<T>, b: Set<T>): Set<T> =>
  new Set([...a].filter((x) => !b.has(x)));

const intersection = <T>(a: Set<T>, b: Set<T>): Set<T> =>
  new Set([...a].filter((x) => b.has(x)));

// SortableList needs a stable id; value is unique per keyword type in practice.
export const createKeyword = (value: string, type: KeywordType): Keyword => {
  return {
    id: value,
    value,
    type,
  };
};

export const loadSelectedKeywords = (product?: IProduct) => {
  if (!product) {
    return [];
  }

  const { final, generated, database } = getKeywordsByType(product);

  const finalKeywords = new Set(keywordsToText(final));
  const databaseKeywords = new Set(keywordsToText(database));
  const generatedKeywords = new Set(keywordsToText(generated));

  const selectedDatabaseKeywords = new Set<string>(
    intersection(finalKeywords, databaseKeywords),
  );
  const selectedGeneratedKeywords = new Set<string>(
    intersection(finalKeywords, generatedKeywords),
  );
  const customKeywords = difference(
    finalKeywords,
    union(databaseKeywords, generatedKeywords),
  );

  return [
    ...[...customKeywords].map((keyword) =>
      createKeyword(keyword, KeywordType.Custom),
    ),
    ...[...selectedDatabaseKeywords].map((keyword) =>
      createKeyword(keyword, KeywordType.Database),
    ),
    ...[...selectedGeneratedKeywords].map((keyword) =>
      createKeyword(keyword, KeywordType.Generated),
    ),
  ];
};

interface KeywordTypeReport {
  type: KeywordType;
  average: number;
  total: number;
  keywords: Keyword["value"][];
}

interface KeywordReport {
  total: number;
  types: KeywordTypeReport[];
}

export type ReportAverageNumberOfKeywordsInFinalSelectionUseCase = (
  keywords: Keyword[],
) => KeywordReport;

export const reportAverageNumberOfKeywordsInFinalSelection: ReportAverageNumberOfKeywordsInFinalSelectionUseCase =
  (keywords) => {
    const total = keywords.length;
    const types = keywords.reduce((acc, keyword) => {
      const { type } = keyword;
      const typeReport = acc.find((report) => report.type === type);
      if (typeReport) {
        typeReport.total += 1;
        typeReport.average = +(typeReport.total / total).toFixed(4);
        typeReport.keywords.push(keyword.value);
      } else {
        acc.push({
          type,
          average: +(1 / total).toFixed(4),
          total: 1,
          keywords: [keyword.value],
        });
      }
      return acc;
    }, [] as KeywordTypeReport[]);

    return {
      total,
      types,
    };
  };

export interface KeywordSelectionReport {
  total_keywords: number;
  database_keywords: string[];
  custom_keywords: string[];
  generated_keywords: string[];
  average_database_keywords: number;
  average_custom_keywords: number;
  average_generated_keywords: number;
  [key: string]: any;
}

export const googleAnalyticsKeywordSelectionReport = (
  product: IProduct | undefined,
): KeywordSelectionReport => {
  const keywords = loadSelectedKeywords(product);
  const report = reportAverageNumberOfKeywordsInFinalSelection(keywords);
  const database = report.types.find(
    (type) => type.type === KeywordType.Database,
  );
  const custom = report.types.find((type) => type.type === KeywordType.Custom);
  const generated = report.types.find(
    (type) => type.type === KeywordType.Generated,
  );

  return {
    total_keywords: report.total ?? 0,
    custom_keywords: custom?.keywords ?? [],
    generated_keywords: generated?.keywords ?? [],
    database_keywords: database?.keywords ?? [],
    average_database_keywords: database?.average ?? 0,
    average_custom_keywords: custom?.average ?? 0,
    average_generated_keywords: generated?.average ?? 0,
  };
};
